import { useState, useEffect, useMemo } from 'react';
import adaptVariation from './adaptVariation';

import { CMS_API_URL } from '../config/apiBase';

/**
 * Fetch a package for a school and adapt each group product into the same shape
 * the product configurator consumes (colors/sizes/attributes/compatibility_tree),
 * so a package supports every option a standalone product does — with the
 * collection's per-product variation config applied.
 *
 * Backed by GET /api/schools/{brandSlug}/{schoolSlug}/packages/{slug}?shop&class.
 * Returns { data: { …package, groups:[{name, products:[adapted]}] }, loading, error }.
 */
export default function usePackageVariation(brandSlug, schoolSlug, slug, className = null, shopSlug = null) {
  const [raw, setRaw]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!brandSlug || !slug) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        let url;
        if (schoolSlug) {
          // School context: variation-shaped products with per-collection config.
          const params = new URLSearchParams();
          if (className) params.set('class', className);
          if (shopSlug)  params.set('shop', shopSlug);
          const qs = params.toString() ? `?${params.toString()}` : '';
          url = `${CMS_API_URL}/api/schools/${encodeURIComponent(brandSlug)}/${encodeURIComponent(schoolSlug)}/packages/${encodeURIComponent(slug)}${qs}`;
        } else {
          // No school yet: fall back to the public package (raw products, already
          // in colors/sizes/attributes shape — no per-collection overrides/tree).
          url = `${CMS_API_URL}/api/packages/${encodeURIComponent(brandSlug)}/${encodeURIComponent(slug)}`;
        }
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`Package API returned ${res.status}`);
        const json = await res.json();
        if (!cancelled) setRaw({ ...json, _scoped: !!schoolSlug });
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; controller.abort(); };
  }, [brandSlug, schoolSlug, slug, className, shopSlug]);

  const data = useMemo(() => {
    if (!raw) return null;
    // Both endpoints now emit variation-shaped products (pinned_attributes /
    // available_options + compatibility_tree + node_images + deliveryInformation),
    // so adapt uniformly into the configurator shape the FE consumes.
    const mapProduct = adaptVariation;
    return {
      id:          raw.id,
      name:        raw.name,
      slug:        raw.slug,
      description: raw.description,
      price:       raw.price,
      priceRaw:    raw.priceRaw,
      images:      Array.isArray(raw.images) ? raw.images : [],
      // The package's own attributes (already in configurator shape) — rendered
      // at the very bottom; their add-on prices add to the package total.
      attributes:  Array.isArray(raw.attributes) ? raw.attributes : [],
      // Groups with no products (all deactivated/deleted) render as empty
      // blocks — the BE drops them too; this guards stale/cached payloads.
      groups: (raw.groups || [])
        .filter((g) => (g.products || []).length > 0)
        .map((g) => ({
          name: g.name,
          products: (g.products || []).map(mapProduct),
        })),
    };
  }, [raw]);

  return { data, loading, error };
}
